import type {PageProps} from 'gatsby'
import {graphql} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import type {LegalPageThirdPartyLicencesQuery} from '@/graphql-types'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import type {GatsbyHeadProps} from '@helios/shared/types/gatsby-head'

import LegalDocumentsGrid from '../legal/documents-grid'
import LegalThirdPartyLicencesContent from '../legal/legal-content-third-party-licences'

type Props = PageProps<LegalPageThirdPartyLicencesQuery>

const LegalPageThirdPartyLicences = (props: Props) => {
    const content = props.data.allPrismicLegalPageThirdPartyLicences.nodes[0].data
    const legalDocsContent = props.data.allPrismicLegalDocuments.nodes[0]

    if (!content || !legalDocsContent) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header />

            <Main>
                <LegalThirdPartyLicencesContent content={content} />

                <LegalDocumentsGrid content={legalDocsContent} />
            </Main>

            <Footer />
        </>
    )
}

export const Head = ({
    location,
    pageContext,
    data,
}: GatsbyHeadProps<LegalPageThirdPartyLicencesQuery>) => {
    const pageData = data.allPrismicLegalPageThirdPartyLicences.nodes[0].data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

export const query = graphql`
    query LegalPageThirdPartyLicences($locale: String!) {
        allPrismicLegalPageThirdPartyLicences(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                data {
                    meta_title
                    meta_description
                    open_graph_image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                    }
                    sections {
                        section_image {
                            alt
                            copyright
                            url
                            dimensions {
                                height
                                width
                            }
                        }
                        section_title
                        section_content {
                            richText
                        }
                        inverse
                    }
                    recognition_title
                    predefined_licences {
                        document {
                            ... on PrismicThirdPartyLicencesSection {
                                data {
                                    licences {
                                        licence_type
                                        program_name
                                        program_url
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        allPrismicLegalDocuments(filter: {lang: {eq: $locale}}) {
            nodes {
                _previewable
                ...LegalDocumentsGrid
            }
        }
    }
`

export default withPrismicPreview(LegalPageThirdPartyLicences)
